// @givmo/app-charity — the unified statement table (CPA-WEB11).
//
// ★ WHY THIS IS HAND-ROLLED OVER THE TABLE PRIMITIVES RATHER THAN <DataTable>. DataTable's row
// disclosure renders a row's children inside ONE `<tr>` spanning every column, which means a nested
// level has to bring its own `<table>` — and a nested table cannot share the parent's column widths.
// The whole legibility of this screen rests on the opposite property: a grant's money, a fundraiser
// bucket's money, and a single donation's money sit in the SAME three columns, so the eye can run down
// Gross and have it mean one thing at every level. So the levels are peer `<tr>`s in one table over a
// flattened row model, which is also what the charity and super-admin tables already do with these
// primitives.
//
// ★ THE VISIBLE HEADER NEVER ENTERS THE VERTICAL SCROLLER. Chrome can compositor-scroll a sticky
// `<th>` for a frame and snap it back even when every settled DOM rect is stable. The fixed window
// therefore has two rails: a presentational visible header above the row scroller, and the native
// body table below it. The body table retains the ONE accessible `<thead>` as a zero-height rail, so
// its cells keep native header association without duplicating anything in the accessibility tree.
// Horizontal pan is bridged in BOTH directions by guarded `scrollLeft` assignment — row panning
// moves the header, while the header's native focus-scroll (its Status explainer can begin off-screen)
// moves the rows. Equality guards stop the reciprocal scroll events from ping-ponging; no React
// state, render, or vertical-position dependency participates in scrolling.
//
// ★ THE TWO RAILS ARE BUILT ALIKE SO THEY SHARE ONE REACHABLE MAXIMUM (CPA-WEB66). Both windows
// reserve the same scrollbar gutter (`scrollbar-gutter: stable`) and both tables end in the same
// presentational gutter column, sized to that gutter — so their canvases are equal and whatever a
// browser does with a gutter it does to both. Measured on a classic-scrollbar Linux Chromium
// (15px gutter, 320×568): a scroll write clamps at `scrollWidth − offsetWidth` for a window that
// reserves a gutter, NOT at `scrollWidth − clientWidth`, and it does so whether or not a vertical
// scrollbar is actually rendered in that gutter. Before this the gutter column was on the header
// alone, so the header's range exceeded the rows' by exactly the gutter and the two rails rested a
// gutter apart at the far end (rows 345 / header 360). Overlay-scrollbar platforms (macOS, phones,
// Firefox on Linux) have a 0px gutter and never showed it.
//
// ★ DISCLOSURE A11Y. Each toggle is a real `<button aria-expanded>`. Each TOP-LEVEL item gets its own
// `<tbody id>`, so its toggle also carries an `aria-controls` pointing at an element that really holds
// every row it reveals (several `<tbody>` in one table is valid HTML). Nested buckets carry
// `aria-expanded` alone — their rows are siblings inside the parent's `<tbody>`, and a dangling IDREF
// is worse than an absent optional one.
// Expansion is CONTROLLED and multi-open, defaulting to everything expanded, so expand-all /
// collapse-all is a set assignment rather than per-row state.
//
// ★ FOCUS = RING **AND** OUTLINE on every control authored here. The tinted `shadow-ring` alone
// composites to 1.23:1 on white — the design system measures this in button.tsx — so each control
// pairs it with the house 2px `brand-500` outline. Two details are load-bearing: the recipe's
// `outline-none` sets `--tw-outline-style: none`, so the SAME variant has to re-arm it with
// `focus-visible:outline-solid` or nothing paints; and controls INSIDE the table's `overflow-x-auto`
// wrapper use the negative (inset) offset, because a positive offset clips against the scroll
// container at the leading cell.
//
// ★ MONEY IS RENDERED, NEVER COMPUTED. Every figure here is `displayMoney(<server string>)`. A group's
// total is its own server block's; it does not change when a filter hides its children — the count
// line says "n of M shown" instead. See statement-decode.ts.

import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';
import {
  Badge,
  Icon,
  Popover,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  testIdProp,
} from '@givmo/design-system';
import { ChevronDown, ChevronRight, ExternalLink, Info } from 'lucide-react';
import type { Section321StatementDonationOut } from '@givmo/api-client';
import { displayMoney } from './grants-decode';
import {
  donationStatusBadge,
  FIRST_COLUMN_LABEL,
  STATUS_CATEGORIES,
  STATUS_EXPLAINER_TITLE,
  STATUS_EXPLAINER_TRIGGER_LABEL,
  UNASSIGNED_GRANT_LABEL,
  UNSHARED_SECONDARY_SUFFIX,
  type StatementTab,
} from './statement-vocab';
import {
  groupCountLabel,
  type StatementGroup,
  type StatementIndex,
  type StatementItem,
  type StatementView,
} from './statement-decode';
import { StatementFundraiserPanel } from './statement-fundraiser-panel';
import { CharityFundraiserReportLink } from '../fundraisers-module/fundraiser-report-link';
import { useLaneAtLeast } from '../../lib/use-lane-width';

export const STATEMENT_PREFIX = 'statement';

/**
 * The lane at which the desktop composition returns — the TABLE'S OWN width, never the viewport's.
 *
 * The ratified desktop set is 876px before the identity column (the 76px toggle, the three money
 * columns at their desktop widths, the trailing columns); with the identity at its phone floor
 * (`STATEMENT_COLUMN_SPEC.phone.identityMin`, 112px) the set needs a 988px lane. Under shell
 * contract v3 every page wears the 256px sidebar, so the viewport that hands the table this lane
 * is about 1292px (988 + 256 + the 48px of page padding) — a viewport query at `xl` (1280) would
 * compose the desktop set into a 976px lane and push Status off the edge at rest. The CSS half is a
 * container query on the table's frame (`@min-[988px]:` / `@max-[988px]:`), the JS half
 * `useLaneAtLeast` on the same frame at the same number, so the measured column order and the CSS
 * variants switch TOGETHER. All four views keep the one boundary: they are one table with different
 * lenses, and a lens that changed composition when the others did not would be a different-feeling
 * screen (plan 18 §3.3).
 */
export const STATEMENT_DESKTOP_LANE_PX = 876 + 112;

/** The trailing columns each view carries, after the three money columns. */
type TrailingColumn = 'designation' | 'fundraiser' | 'status';

const TRAILING_COLUMNS: Record<StatementTab, readonly TrailingColumn[]> = {
  // Every view carries the Status column, so the three money columns sit at one x in every lens
  // (D-CP-55, Jonathan 2026-09-04: By grant was the one view without it, and its Gross / Fees / Net
  // stood 126px right of the other views' at 1440). What the column holds in By grant is the GRANT's
  // status, on the grant's own row — Jonathan's final wording: "the whole concept of a grant is when
  // donations are grouped together into one transaction that gets sent to the charity - so there is
  // no point in having individual donation status on the grant table - the grant status is what
  // should be in the far right status column". The donation rows beneath a grant leave the cell
  // empty, and the grant row's identity names the transaction instead of restating the status.
  all: ['designation', 'status'],
  grant: ['designation', 'status'],
  fundraiser: ['designation', 'status'],
  donor: ['designation', 'fundraiser', 'status'],
};

const TRAILING_HEADER: Record<TrailingColumn, string> = {
  designation: 'Designation',
  fundraiser: 'Fundraiser',
  status: 'Status',
};

// ⚠ BORDER-BOX, so each value is the design's content width PLUS the cell's own `px-16` (32px).
// The design specifies content widths (`width:110px` beside `padding:0 16px`, the CSS default
// content-box); Tailwind's preflight puts every element in `border-box`, so declaring the design's
// number directly makes every column 32px narrow — and the error compounds leftward across the row,
// which is exactly how it first showed up (money columns creeping left, the designation wrapping).
const TRAILING_WIDTH: Record<TrailingColumn, string> = {
  designation: 'w-[192px]',
  fundraiser: 'w-[212px]',
  status: 'min-w-[202px]',
};

/** Every column the table can carry; `columnOrder` says which, in what order, for a view. */
type ColumnKey = 'toggle' | 'identity' | 'gross' | 'fee' | 'net' | TrailingColumn;

/**
 * ONE column order per (view, breakpoint), and everything that must agree by index reads it: the
 * presentational rail's `<col>`s and cells, the semantic header, and every row renderer — the
 * header mirror copies widths by index, so a renderer that composed its own order would desync it.
 *
 * From the desktop lane up this is Jonathan's ratified canvas: identity · Gross · Fees · Net to you · the view's
 * trailing columns. Below the desktop lane (see `STATEMENT_DESKTOP_LANE_PX` for why the boundary is the
 * lane's, not the shell's `lg`) the one per-row fact a reconciler needs — status — was the last
 * column, two swipes away on a phone (cert R1-B-4). There the money that matters leads and status
 * sits beside it: identity · Net to you · Status · Gross · Fees · the rest, in their ratified order —
 * the one composition for all four views, since every view carries Status (D-CP-55).
 */
export function columnOrder(tab: StatementTab, compact: boolean): readonly ColumnKey[] {
  const trailing = TRAILING_COLUMNS[tab];
  if (!compact) return ['toggle', 'identity', 'gross', 'fee', 'net', ...trailing];
  const status: readonly ColumnKey[] = trailing.includes('status') ? ['status'] : [];
  const rest = trailing.filter((column) => column !== 'status');
  return ['toggle', 'identity', 'net', ...status, 'gross', 'fee', ...rest];
}

const HEADER_ID: Record<ColumnKey, string> = {
  toggle: 'toggle',
  identity: 'identity',
  gross: 'gross',
  fee: 'fees',
  net: 'net',
  designation: 'designation',
  fundraiser: 'fundraiser',
  status: 'status',
};

// ── The column spec below the desktop lane ──────────────────────────────────────────────────────────────────────
//
// Below the desktop lane the toggle + identity pair is PINNED and every other column carries an explicit width,
// so the pan has an arithmetic the eye can rely on (cert R2-A-3 / R2-B-2):
//
//   · The next column PEEKS at the lane's right edge — the visible set (pinned pair · Net · Status)
//     never ends on a column boundary, so the cut itself says "more to the right" (Stripe's cue) and
//     no sentence has to.
//   · The LAST column is never wider than the lane beside the pinned pair, so at maximum pan its left
//     edge lands exactly on the pair's right edge instead of under it ("ment fund", cert R2-B-2).
//
// Both are derived from the REAL lane through container-query units — the table frame is an
// inline-size container, so `100cqw` is the lane. Nothing here is a per-device number, and
// `STATEMENT_COLUMN_SPEC` carries the same figures for the geometry pin. From the desktop lane up every value
// returns to the ratified desktop layout.
//
// Phone (below `sm`): the pinned pair and Net are whole at rest, and the lane's edge falls INSIDE the
// column after Net — Status, in every view — with `edgeCut` (24px) of it past the edge. The
// identity takes everything before that column, `lane − (toggle + Net + Status − edgeCut)`, never
// less than 112px, which is where a nested "Direct donations" label still
// keeps its words whole beside its chevron. So the identity GROWS with the phone (112px at 320, 122px
// at 360, 152px at 390) instead of being squeezed for a sliver of Gross, and every phone width shows
// the same cue. Net keeps a compact 84px cell and Status an 88px cell whose badge wraps a long
// server-authored label such as "Awaiting next transfer" in full rather than truncating it, with only
// its decorative dot standing down; Gross and Fees take 88px cells and Net 96px, each holding a
// six-figure amount (see the spec). The phone's last column is `lane − (toggle + identity)`, so at
// maximum pan it lands on the pair.
//
// Tablet, landscape and the narrow desktop (`sm` to the desktop lane): the desktop money widths (By donor's own,
// narrower set in that lens), a pinned 44px toggle, and an identity derived PER VIEW (By donor's
// narrower Net moves its offset) so the visible set — Net and Status — leaves 80px of Gross
// showing. Enough of a right-aligned figure to
// cut through its digits, with a floor that wraps the widest fixture name to two lines and a ceiling
// at its own desktop width. Once the ceiling binds (lanes from ~774–834 depending on the view, i.e.
// the sidebar desktop from ~1140 wide) the identity stops absorbing the lane and the money columns
// come fully into view in turn, so the cut then drifts across column boundaries instead of holding
// at 80px — the trade-off the ceiling buys is a bounded identity, not a fixed cue, and from the desktop lane the
// ratified set fits without one.
export const STATEMENT_COLUMN_SPEC = {
  phone: {
    toggle: 28,
    // Sized for a six-figure amount, not the fixture: at 13px `$123,456.78` is 75.7px and, at the
    // group row's 600 weight, `$182,917.00` 76.3px. A fixed-layout cell cannot grow and a currency
    // string has no break opportunity, so the cell must hold the figure — 88 − 8 = 80px of content
    // for Gross and Fees, 96 − 16 = 80px for Net (review F2).
    net: 96,
    status: 88,
    gross: 88,
    fee: 88,
    // The lane's edge falls INSIDE the column after Net (Status): at rest this much of it is past
    // the edge, and the identity takes everything before it, never less than `identityMin`.
    edgeCut: 24,
    identityMin: 112,
  },
  tablet: {
    toggle: 44,
    status: 202,
    peek: 80,
    identityMin: 160,
    identityMax: 366,
    // Below `lg` the money cells keep their desktop widths, and the By-donor lens has its own.
    money: {
      default: { gross: 142, fee: 122, net: 142 },
      donor: { gross: 110, fee: 90, net: 110 },
    },
  },
} as const;

// `cqw` resolves against the nearest ANCESTOR container, so the variable lives on the table window
// (a child of the frame), never on the frame itself. LITERAL strings — Tailwind reads class strings
// as text — one per distinct offset. Phone: toggle + Net + Status − edgeCut (28 + 96 + 88 − 24), the
// same in every view. Tablet: toggle + the view's own Net + Status + peek: 44 + 142 + 202 + 80 in
// All / By grant / By fundraiser, 44 + 110 + 202 + 80 in By donor. The geometry pin re-derives every
// figure from `STATEMENT_COLUMN_SPEC`.
const DEFAULT_IDENTITY_WIDTH_VARIABLE =
  '[--stmt-identity-w:max(112px,calc(100cqw_-_188px_-_var(--stmt-gutter-w,0px)))] sm:[--stmt-identity-w:clamp(160px,calc(100cqw_-_468px_-_var(--stmt-gutter-w,0px)),366px)]';
export const IDENTITY_WIDTH_VARIABLES: Record<StatementTab, string> = {
  all: DEFAULT_IDENTITY_WIDTH_VARIABLE,
  fundraiser: DEFAULT_IDENTITY_WIDTH_VARIABLE,
  donor:
    '[--stmt-identity-w:max(112px,calc(100cqw_-_188px_-_var(--stmt-gutter-w,0px)))] sm:[--stmt-identity-w:clamp(160px,calc(100cqw_-_436px_-_var(--stmt-gutter-w,0px)),366px)]',
  grant: DEFAULT_IDENTITY_WIDTH_VARIABLE,
};

/** The column the phone lane cuts at rest: the one right after Net in this view's compact order — Status. */
export function phoneCutColumn(tab: StatementTab): ColumnKey {
  const compact = columnOrder(tab, true);
  return compact[compact.indexOf('net') + 1] as ColumnKey;
}

/** The column the tablet lane cuts at rest: the one after Status — Gross, 80px of it showing. */
export function tabletCutColumn(tab: StatementTab): ColumnKey {
  const compact = columnOrder(tab, true);
  return compact[compact.indexOf('status') + 1] as ColumnKey;
}

export function identityWidthVariable(tab: StatementTab): string {
  return IDENTITY_WIDTH_VARIABLES[tab];
}
const IDENTITY_WIDTH =
  'w-[var(--stmt-identity-w)] min-w-[var(--stmt-identity-w)] max-w-[var(--stmt-identity-w)]';
/**
 * The phone's last column: exactly the lane beside the pinned pair, so max pan clears it. `100cqw`
 * is the frame, and a classic (non-overlay) vertical scrollbar makes the scrollport narrower by its
 * gutter; the column mirror measures that gutter into `--stmt-gutter-w` on the window and every
 * lane-derived width subtracts it, so the arithmetic holds with real scrollbars too (review F4).
 */
export const LAST_COLUMN_PHONE =
  'max-sm:w-[calc(100cqw_-_28px_-_var(--stmt-identity-w)_-_var(--stmt-gutter-w,0px))] max-sm:min-w-[calc(100cqw_-_28px_-_var(--stmt-identity-w)_-_var(--stmt-gutter-w,0px))] max-sm:max-w-[calc(100cqw_-_28px_-_var(--stmt-identity-w)_-_var(--stmt-gutter-w,0px))]';

// Literal class strings (Tailwind reads source as text) — the `STATEMENT_COLUMN_SPEC` numbers, pinned
// against these by the compact-cell recipe test so the two cannot drift apart (review F5).
export const COMPACT_NET_CELL =
  'max-sm:w-[96px] max-sm:min-w-[96px] max-sm:max-w-[96px] max-sm:px-8';
export const COMPACT_STATUS_CELL =
  'max-sm:w-[88px] max-sm:min-w-[88px] max-sm:max-w-[88px] max-sm:px-4';
export const COMPACT_MONEY_CELL =
  'max-sm:w-[88px] max-sm:min-w-[88px] max-sm:max-w-[88px] max-sm:px-4';
const COMPACT_STATUS_BADGE =
  'max-sm:max-w-[80px] [&>span:first-child]:hidden sm:[&>span:first-child]:block';
const COLUMN_HEADER_CELL = 'bg-neutral-50';
const SEMANTIC_HEADER_CELL = 'h-0 border-0 p-0 leading-none';

/**
 * The By-donor lens is over-subscribed, and it is the only one that is.
 *
 * It is the only 8-column view. At the design's widths it needs, inside a ~1134px card:
 *
 *   toggle 76 · donor ~230 · gross 142 · fees 122 · net 142 · designation ~150
 *   · fundraiser ~210 · status 202                                     =  ~1274px   (~140 over)
 *
 * `table-layout: auto` resolves that by compressing, which is why the text columns wrap THERE and
 * nowhere else. Two cures were tried and MEASURED before this one:
 *
 *   · Dropping the fixed width on designation/fundraiser so they take a share of the remainder made
 *     it WORSE — auto layout gives an unsized column its MIN-CONTENT width, so both collapsed to
 *     their longest word and wrapped to three lines instead of two.
 *   · Letting the lens pan (`min-w` above the card) kept every column at full width and wrapped
 *     nothing, but pushed STATUS off-screen — hiding a column the design deliberately placed is
 *     worse than wrapping text in one.
 *
 * So the donor lens spends its budget where the content actually needs it. The MONEY columns are
 * demonstrably over-provisioned — "$1,300.00" is ~70px in a 110px content box — and the status column
 * holds a short badge or a dated sent label. Trimming those five buys the ~140px the two free-text
 * columns need:
 *
 *   76 + 110 + 90 + 110 + 192 + 212 + 202 = 992  →  ~142 for the donor column, where the label's
 *   second line may wrap. Two lines is already the design's form for a donor header, so the one
 *   remaining wrap lands in the one place it costs nothing.
 *
 * STATUS keeps the design-matched 202 deliberately: the dated sent form can include a year when the
 * covered period crosses one, and it is the last factual column that should give width up. Trimming it
 * to 170 was measured and made those badges visibly worse.
 *
 * Everything stays visible, at its own width, with the money columns still aligned down the page.
 */
export const DONOR_MONEY_WIDTH = { gross: 'w-[110px]', fee: 'w-[90px]', net: 'w-[110px]' } as const;
export const DEFAULT_MONEY_WIDTH = {
  gross: 'w-[142px]',
  fee: 'w-[122px]',
  net: 'w-[142px]',
} as const;

// Wherever the table pans (everything below the desktop lane) these two columns stay put — a row's identity is
// never scrolled away from the number being read (cert R2-B-2 measured `position: relative` at 768
// and 844). The toggle is a fixed 28px slot on the phone and 44px from `sm`; the identity takes the
// derived width above. From the desktop lane up body cells return to ordinary table layout and visible header
// cells drop their horizontal pin. The visible rail itself is outside the vertical scroller, so no
// header cell needs a vertical sticky position at any width.
const MOBILE_TOGGLE_HEADER =
  'sticky left-0 z-40 w-[28px] min-w-[28px] max-w-[28px] bg-neutral-50 px-2 sm:w-[44px] sm:min-w-[44px] sm:max-w-[44px] sm:px-0 @min-[988px]:static @min-[988px]:z-30 @min-[988px]:w-[76px] @min-[988px]:min-w-0 @min-[988px]:max-w-none @min-[988px]:px-16';
const MOBILE_IDENTITY_HEADER = `sticky left-[28px] z-40 ${IDENTITY_WIDTH} border-r border-border bg-neutral-50 px-8 sm:left-[44px] sm:px-16 @min-[988px]:static @min-[988px]:z-30 @min-[988px]:w-auto @min-[988px]:min-w-0 @min-[988px]:max-w-none @min-[988px]:border-r-0`;
const MOBILE_TOGGLE_SEMANTIC =
  'w-[28px] min-w-[28px] max-w-[28px] sm:w-[44px] sm:min-w-[44px] sm:max-w-[44px] @min-[988px]:w-[76px] @min-[988px]:min-w-0 @min-[988px]:max-w-none';
const MOBILE_IDENTITY_SEMANTIC = `${IDENTITY_WIDTH} @min-[988px]:w-auto @min-[988px]:min-w-0 @min-[988px]:max-w-none`;
export const MOBILE_TOGGLE_CELL =
  'sticky left-0 z-10 w-[28px] min-w-[28px] max-w-[28px] bg-surface px-2 sm:w-[44px] sm:min-w-[44px] sm:max-w-[44px] sm:px-0 @min-[988px]:static @min-[988px]:w-auto @min-[988px]:min-w-0 @min-[988px]:max-w-none @min-[988px]:px-16';
const MOBILE_IDENTITY_CELL = `sticky left-[28px] z-10 ${IDENTITY_WIDTH} border-r border-border bg-surface px-8 sm:left-[44px] sm:px-16 @min-[988px]:relative @min-[988px]:left-auto @min-[988px]:w-auto @min-[988px]:min-w-0 @min-[988px]:max-w-none @min-[988px]:border-r-0`;

function moneyWidths(tab: StatementTab) {
  return tab === 'donor' ? DONOR_MONEY_WIDTH : DEFAULT_MONEY_WIDTH;
}

/** A trailing column's width; the LAST column below `sm` is bound to the lane (see the spec). */
function trailingWidth(column: TrailingColumn, last: boolean): string {
  return last ? `${TRAILING_WIDTH[column]} ${LAST_COLUMN_PHONE}` : TRAILING_WIDTH[column];
}

function headerLabel(key: ColumnKey, tab: StatementTab): string {
  switch (key) {
    case 'toggle':
      return 'Expand or collapse';
    case 'identity':
      return FIRST_COLUMN_LABEL[tab];
    case 'gross':
      return 'Gross';
    case 'fee':
      return 'Fees';
    case 'net':
      return 'Net to you';
    default:
      return TRAILING_HEADER[key];
  }
}

/**
 * The width recipe a column's cells carry in BOTH rails (the semantic header and the visible one).
 * `last` is whether the column ends the view's order — the phone binds that one to the lane.
 */
function columnWidth(key: ColumnKey, tab: StatementTab, last = false): string {
  switch (key) {
    case 'toggle':
    case 'identity':
      return '';
    case 'gross':
      return `${moneyWidths(tab).gross} ${COMPACT_MONEY_CELL}`;
    case 'fee':
      return `${moneyWidths(tab).fee} ${COMPACT_MONEY_CELL}`;
    case 'net':
      return `${moneyWidths(tab).net} ${COMPACT_NET_CELL}`;
    case 'status':
      // Below the desktop lane the body table is fixed-layout (see the Table below), where only `width` sizes a
      // column: the desktop `min-w` alone would give Status nothing.
      return `${trailingWidth('status', last)} @max-[988px]:w-[202px] ${COMPACT_STATUS_CELL}`;
    default:
      return trailingWidth(key, last);
  }
}

function synchronizeScrollLeft(source: HTMLDivElement, target: HTMLDivElement | null) {
  if (target && target.scrollLeft !== source.scrollLeft) target.scrollLeft = source.scrollLeft;
}

/**
 * The shell declares its sticky app-bar height as this custom property on its root and sizes the
 * bar from it, so the persistent chrome this table must stay clear of has exactly one source. Only
 * a px value counts: any other unit — or no hosting shell at all — reads as no bar, by construction.
 */
const APP_BAR_HEIGHT_PROPERTY = '--charity-app-bar-h';

function readAppBarHeight(element: HTMLElement): number {
  const declared = getComputedStyle(element).getPropertyValue(APP_BAR_HEIGHT_PROPERTY).trim();
  if (!declared.endsWith('px')) return 0;
  const value = Number.parseFloat(declared);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

/** True once the page has vertical scroll range and has used all of it — Y cannot move further. */
function documentAtScrollMax(): boolean {
  const maxScrollY = document.documentElement.scrollHeight - window.innerHeight;
  return maxScrollY > 0 && window.scrollY >= maxScrollY - 1;
}

function VisibleColumns({ columnCount }: { columnCount: number }) {
  return (
    <colgroup>
      {Array.from({ length: columnCount }, (_, index) => (
        <col key={index} {...testIdProp(`${STATEMENT_PREFIX}-visible-column`)} />
      ))}
      <col {...testIdProp(`${STATEMENT_PREFIX}-scrollbar-gutter-column`)} />
    </colgroup>
  );
}

/**
 * The body's trailing gutter column — the twin of the visible rail's, sized by the gutter the window
 * publishes (`--stmt-gutter-w`, the same measurement the visible rail's column is written from), so
 * the two tables' canvases are equal (CPA-WEB66, the file header). A `<col>` rather than a header
 * cell: the semantic `<thead>` is the ONE accessible header set and keeps exactly the data columns.
 */
const BODY_GUTTER_COLUMN = 'w-[var(--stmt-gutter-w,0px)]';
// The cells pin the column EXACTLY: from the desktop lane the body is auto-layout, and a `<col>`
// width alone is a preference auto layout compresses with the rest when the lane is tight
// (measured: 7.66px of a 15px gutter at 1440) — a cell's min/max width is a floor it cannot cross.
const BODY_GUTTER_CELL =
  'p-0 w-[var(--stmt-gutter-w,0px)] min-w-[var(--stmt-gutter-w,0px)] max-w-[var(--stmt-gutter-w,0px)]';

function BodyColumns({ columnCount }: { columnCount: number }) {
  return (
    <colgroup>
      {Array.from({ length: columnCount }, (_, index) => (
        <col key={index} />
      ))}
      <col
        className={BODY_GUTTER_COLUMN}
        {...testIdProp(`${STATEMENT_PREFIX}-body-gutter-column`)}
      />
    </colgroup>
  );
}

/** A row's cell in the body's gutter column: nothing in it, so the row's rule runs through it. */
function BodyGutterCell() {
  return (
    <td
      role="presentation"
      aria-hidden
      className={BODY_GUTTER_CELL}
      {...testIdProp(`${STATEMENT_PREFIX}-body-gutter-cell`)}
    />
  );
}

/** The column the phone binds to the lane: the last of the COMPACT order, whatever order renders. */
function phoneLastColumn(tab: StatementTab): ColumnKey {
  const compact = columnOrder(tab, true);
  return compact[compact.length - 1] as ColumnKey;
}

function SemanticHeader({ tab, columns }: { tab: StatementTab; columns: readonly ColumnKey[] }) {
  const last = phoneLastColumn(tab);
  const width = (key: ColumnKey) =>
    key === 'toggle'
      ? MOBILE_TOGGLE_SEMANTIC
      : key === 'identity'
        ? MOBILE_IDENTITY_SEMANTIC
        : columnWidth(key, tab, key === last);

  return (
    <THead className="h-0 bg-transparent" {...testIdProp(`${STATEMENT_PREFIX}-semantic-header`)}>
      <tr className="h-0">
        {columns.map((key) => (
          <TH
            key={key}
            id={`${STATEMENT_PREFIX}-header-${HEADER_ID[key]}`}
            className={`${SEMANTIC_HEADER_CELL} ${width(key)}`}
          >
            <span className="sr-only">{headerLabel(key, tab)}</span>
          </TH>
        ))}
      </tr>
    </THead>
  );
}

function VisibleHeader({ tab, columns }: { tab: StatementTab; columns: readonly ColumnKey[] }) {
  // Every width here is content + the cell's own 32px of padding — see TRAILING_WIDTH.
  const last = phoneLastColumn(tab);
  const cell = (key: ColumnKey) => {
    switch (key) {
      case 'toggle':
        return <TH key={key} role="presentation" className={MOBILE_TOGGLE_HEADER} />;
      case 'identity':
        return (
          <TH key={key} role="presentation" className={MOBILE_IDENTITY_HEADER}>
            <span aria-hidden>{FIRST_COLUMN_LABEL[tab]}</span>
          </TH>
        );
      case 'gross':
      case 'fee':
      case 'net':
        return (
          <TH
            key={key}
            role="presentation"
            align="right"
            className={`${COLUMN_HEADER_CELL} ${columnWidth(key, tab, key === last)}`}
          >
            <span aria-hidden>{headerLabel(key, tab)}</span>
          </TH>
        );
      case 'status':
        return (
          <TH
            key={key}
            role="presentation"
            className={`${COLUMN_HEADER_CELL} ${columnWidth(key, tab, key === last)}`}
          >
            <StatusHeader labelHidden />
          </TH>
        );
      default:
        return (
          <TH
            key={key}
            role="presentation"
            className={`${COLUMN_HEADER_CELL} ${columnWidth(key, tab, key === last)}`}
          >
            <span aria-hidden>{TRAILING_HEADER[key]}</span>
          </TH>
        );
    }
  };

  return (
    <Fragment>
      <VisibleColumns columnCount={columns.length} />
      <THead role="presentation">
        <tr role="presentation">
          {columns.map(cell)}
          <TH
            role="presentation"
            aria-hidden
            className="h-[40px] w-0 min-w-0 border-b border-border-subtle bg-neutral-50 p-0"
            {...testIdProp(`${STATEMENT_PREFIX}-scrollbar-gutter-cell`)}
          />
        </tr>
      </THead>
    </Fragment>
  );
}

export interface StatementTableProps {
  tab: StatementTab;
  view: StatementView;
  index: StatementIndex;
  charityId: string;
  formatDate: (iso: string | null | undefined) => string;
  /**
   * The width of a donation row's date box — `statementDateBoxClass(covered_period)`. It travels
   * WITH `formatDate` because it is the same decision: a box wide enough for the form that formatter
   * emits (CPA-WEB61). The no-year box binds from `sm`, the year-bearing one only in the desktop
   * lane; `statement-vocab.ts` carries why they differ.
   */
  dateBoxClass: string;
  expandedKeys: ReadonlySet<string>;
  onToggle: (key: string) => void;
  /** Whether the flag-gated fundraiser message/organizer panel may render at all. */
  fundraiserPanelsEnabled: boolean;
  /** Rendered inside the fixed row window when filters leave no statement items. */
  emptyState?: ReactNode;
}

export function StatementTable({
  tab,
  view,
  index,
  charityId,
  formatDate,
  dateBoxClass,
  expandedKeys,
  onToggle,
  fundraiserPanelsEnabled,
  emptyState,
}: StatementTableProps) {
  // Below the desktop lane the column order changes (see `columnOrder`), measured off the frame
  // the CSS container variants read; jsdom and a server render measure nothing and get the desktop
  // order.
  const frameRef = useRef<HTMLDivElement | null>(null);
  const compact = !useLaneAtLeast(frameRef, STATEMENT_DESKTOP_LANE_PX);
  const columns = useMemo(() => columnOrder(tab, compact), [tab, compact]);
  const columnCount = columns.length;
  const tableWindowRef = useRef<HTMLDivElement>(null);
  const headerWindowRef = useRef<HTMLDivElement>(null);
  const rowWindowRef = useRef<HTMLDivElement>(null);

  // `view` is memoized by the page from the statement + lens/search/sort/chips. It changes for every
  // meaningful result change, but NOT for expand/collapse, so reconciliation work starts at the top
  // without a parent-chevron click unexpectedly moving the charity's place in the rows. Reset on a
  // CHANGE of view only, never on mount: a freshly mounted window is already at its top, and the
  // page's `?grant=` arrival scroll runs in the same effect flush as this mount — React's StrictMode
  // replay of mount effects would otherwise zero the rows right after that landing (CPA-WEB36).
  const renderedView = useRef(view);
  useEffect(() => {
    if (renderedView.current === view) return;
    renderedView.current = view;
    if (rowWindowRef.current) rowWindowRef.current.scrollTop = 0;
  }, [view]);

  // What the visible rail's `<col>` widths were last measured FOR — the view, the lane and the
  // column set — so the empty-view rule below can tell "the same lane, no rows" (hold the last
  // populated geometry) from "a different lane, no rows" (release the rail to its own layout).
  // `null` once released, so the next populated view mirrors unconditionally. See
  // `synchronizeColumns`.
  const mirroredFor = useRef<string | null>(null);

  // Native listeners are deliberate here. Chrome can focus-scroll an `overflow-hidden` ancestor
  // before focus reaches its off-screen descendant without delivering a useful React `onScroll`.
  // `focusin` sees that new scrollLeft synchronously; ordinary scroll events cover every other
  // source. Equality guards in `synchronizeScrollLeft` make the reciprocal events settle at once.
  useLayoutEffect(() => {
    const rowWindow = rowWindowRef.current;
    const headerWindow = headerWindowRef.current;
    if (!rowWindow || !headerWindow) return;

    const rowsToHeader = () => synchronizeScrollLeft(rowWindow, headerWindow);
    const headerToRows = () => synchronizeScrollLeft(headerWindow, rowWindow);
    rowWindow.addEventListener('scroll', rowsToHeader);
    headerWindow.addEventListener('scroll', headerToRows);
    headerWindow.addEventListener('focusin', headerToRows);
    rowsToHeader();

    return () => {
      rowWindow.removeEventListener('scroll', rowsToHeader);
      headerWindow.removeEventListener('scroll', headerToRows);
      headerWindow.removeEventListener('focusin', headerToRows);
    };
  }, []);

  // A phone first reaches this fixed-height work area while only its top strip is on-screen. If the
  // row scroller owns Y at that moment, a wheel/trackpad gesture moves hidden rows while the page —
  // and the rest of the window — stays put. Page-first arming makes vertical ownership explicit:
  // X panning is always live, but rows own Y only once the whole fixed window sits in the UNOBSCURED
  // viewport — below the shell's sticky app bar, whose height is read once from the token the shell
  // declares (0 when nothing hosts the table). "Inside the browser viewport" is not "visible to the
  // reader" for the strip under that bar. IntersectionObserver is the ordinary boundary signal, with
  // the bar folded into its root; a passive page-scroll reconciliation also covers End/scrollbar-drag
  // jumps that skip its threshold-1 frame and is the no-IO path. Both apply one predicate and one
  // valve: rows also own Y once the document is at its scroll max, because a page that cannot move
  // further has nothing left to hand over — so the handoff can never deadlock. The hinge cannot trap
  // either: armed rows never contain overscroll, so a boundary gesture chains back to the page. Both
  // mutate DOM properties directly and never render on scroll.
  useLayoutEffect(() => {
    const tableWindow = tableWindowRef.current;
    const rowWindow = rowWindowRef.current;
    if (!tableWindow || !rowWindow) return;

    const appBarHeight = readAppBarHeight(tableWindow);
    const applyVerticalOwnership = (rowsOwnY: boolean) => {
      const next = rowsOwnY ? 'armed' : 'page';
      if (rowWindow.dataset.verticalScroll === next) return;
      rowWindow.dataset.verticalScroll = next;
      rowWindow.style.overflowY = rowsOwnY ? 'auto' : 'hidden';
      // Never `contain`. Armed rows own mid-list scrolling natively (a nested scroller keeps a
      // gesture until it hits a boundary), and a gesture AT a boundary must chain to the page: on a
      // phone the window covers the thumb zone, so that chain is the reader's only way back up to
      // the totals. `contain` here trapped the page at its bottom for good (cert R1-B-1).
      rowWindow.style.overscrollBehaviorY = 'auto';
    };
    const fullyVisible = (
      rect: DOMRectReadOnly,
      rootTop = appBarHeight,
      rootBottom = window.innerHeight,
    ) => rect.height > 0 && rect.top >= rootTop - 1 && rect.bottom <= rootBottom + 1;
    const rowsOwnY = (rect: DOMRectReadOnly, rootTop?: number, rootBottom?: number) =>
      fullyVisible(rect, rootTop, rootBottom) || documentAtScrollMax();
    const reconcile = () => applyVerticalOwnership(rowsOwnY(tableWindow.getBoundingClientRect()));

    reconcile();
    const onResize = () => reconcile();
    const onPageScroll = () => reconcile();
    window.addEventListener('resize', onResize);
    // Keep this even when IO exists: End/scrollbar-drag can jump past the exact threshold-1 frame.
    window.addEventListener('scroll', onPageScroll, { passive: true });

    let observer: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          const rootTop = entry.rootBounds?.top ?? appBarHeight;
          const rootBottom = entry.rootBounds?.bottom ?? window.innerHeight;
          applyVerticalOwnership(rowsOwnY(entry.boundingClientRect, rootTop, rootBottom));
        },
        // The negative top margin shrinks the root to the viewport below the app bar, so the
        // entry's `rootBounds` carries the same unobscured top edge the passive path reasons about.
        { threshold: [1], rootMargin: `${-appBarHeight}px 0px 0px 0px` },
      );
      observer.observe(tableWindow);
    }

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onPageScroll);
      observer?.disconnect();
    };
  }, []);

  // Keep the content-aware body layout: a long donor identity may legitimately make that table
  // wider than its scrollport, and forcing a fixed body grid makes the text collide with Gross.
  // The external visible rail is fixed-layout, so copy the body's ACTUAL column geometry into its
  // `<col>` elements before paint and whenever any column resizes. Like the guarded two-way scroll
  // bridge below, this is an imperative DOM synchronization path — it never enters React state.
  useLayoutEffect(() => {
    const rowWindow = rowWindowRef.current;
    const headerWindow = headerWindowRef.current;
    const bodyTable = rowWindow?.querySelector('table');
    const headerTable = headerWindow?.querySelector('table');
    const bodyHeaders = bodyTable?.querySelectorAll('thead th');
    const headerColumns = headerTable?.querySelectorAll('col');
    if (!rowWindow || !headerWindow || !bodyTable || !headerTable || !bodyHeaders || !headerColumns)
      return;

    const synchronizeColumns = () => {
      // The body may reserve a classic vertical scrollbar; every lane-derived width (the identity,
      // the phone's last column and the body's own gutter column) subtracts or takes that gutter
      // through `--stmt-gutter-w`, published on the window before anything else so it is present
      // even over an empty lane (review F4).
      const scrollbarWidth = Math.max(0, rowWindow.offsetWidth - rowWindow.clientWidth);
      tableWindowRef.current?.style.setProperty('--stmt-gutter-w', `${scrollbarWidth}px`);
      // An EMPTY body is never a mirror source. With no data rows its cells collapse — at desktop
      // widths auto layout gives the identity's `w-auto` column nothing and hands its slack to
      // Status (measured: identity 0px, Status 460px at 1440) — and mirroring that bunches the
      // visible titles into a corner above the empty state (cert R1-A-4). Two honest states remain:
      //   · The SAME view, lane and column set the rail was last measured in (a no-match search a
      //     keystroke after rows): HOLD the last populated geometry, so the titles read exactly as
      //     they did over the rows.
      //   · Anything else with no rows (a fresh empty mount; a viewport or view change while the
      //     search matches nothing): RELEASE the rail to its own layout. Its cells carry the same
      //     width recipe the body's do, so below the desktop lane it lays out at the spec's widths for the new
      //     lane, and from the desktop lane its fixed algorithm splits the slack between the identity and Status
      //     instead of collapsing one. A rail frozen at another lane's widths was stale, not stable —
      //     at 390 it stayed laid out for 1440, its titles over columns no longer in that order
      //     (review F9).
      const measuredFor = `${tab}:${rowWindow.clientWidth}:${columns.join(',')}`;
      if (view.items.length === 0) {
        if (mirroredFor.current === measuredFor) return;
        headerTable.style.width = '';
        headerTable.style.minWidth = '';
        headerColumns.forEach((column) => {
          column.style.width = '';
        });
        mirroredFor.current = null;
        synchronizeScrollLeft(rowWindow, headerWindow);
        return;
      }
      // Read AFTER the property is published: the read lays the body out with its gutter column at
      // the gutter's width, so this is the whole canvas, data columns plus gutter.
      const bodyWidth = bodyTable.getBoundingClientRect().width;
      if (bodyWidth <= 0 || bodyHeaders.length + 1 !== headerColumns.length) return;
      mirroredFor.current = measuredFor;
      // Both tables end in the same gutter column, so the header's canvas is the body's, EXACTLY —
      // never the body plus a gutter, which is what left the header a gutter longer than the rows
      // under a classic scrollbar (CPA-WEB66, the file header). Every header column mirrors the
      // body's as laid out, the gutter column included: it reads the body's gutter cell rather than
      // the measured gutter so the two canvases agree to the pixel whatever the layout did (the
      // cell pins that width to the gutter, so the two numbers are one number in practice).
      headerTable.style.width = `${bodyWidth}px`;
      headerTable.style.minWidth = `${bodyWidth}px`;
      bodyHeaders.forEach((cell, index) => {
        const column = headerColumns.item(index);
        if (column) column.style.width = `${cell.getBoundingClientRect().width}px`;
      });
      const gutterColumn = headerColumns.item(headerColumns.length - 1);
      const bodyGutterCell = bodyTable.querySelector<HTMLTableCellElement>(
        `tbody [data-testid="${STATEMENT_PREFIX}-body-gutter-cell"]`,
      );
      if (gutterColumn) {
        gutterColumn.style.width = `${bodyGutterCell?.getBoundingClientRect().width ?? scrollbarWidth}px`;
      }
      synchronizeScrollLeft(rowWindow, headerWindow);
    };

    synchronizeColumns();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(synchronizeColumns);
    observer.observe(bodyTable);
    observer.observe(rowWindow);
    bodyHeaders.forEach((cell) => observer.observe(cell));
    return () => observer.disconnect();
  }, [expandedKeys, tab, view, columns]);

  return (
    <div
      ref={frameRef}
      // The container every lane-derived width (`100cqw`) and every desktop-set variant
      // (`@min-[988px]:` / `@max-[988px]:`) resolves against — the table's own lane.
      className="flex min-h-0 flex-1 flex-col [container-type:inline-size]"
      {...testIdProp(`${STATEMENT_PREFIX}-table-frame`)}
    >
      <div
        ref={tableWindowRef}
        // The cap keeps document End honest: at scroll max the window's top sits at
        // bar + 32px − spaceBelow, so `100dvh` minus the app bar and 32px lands the header at or below
        // the bar. 32 = the measured 25px below this window (the page's 24px bottom padding + the
        // card's 1px border; 25.5 at ≥1280) plus a deliberate ~7px clearance, so sub-pixel layout can
        // never tuck the rail's top edge under the bar and the two hairlines never touch.
        // ⚠ Precondition: the TOTAL space between this window's bottom and the document's end must
        // never exceed 32px (today 25px / 25.5px at ≥1280 — about 7px of headroom); anything more below
        // the table on this route puts the title band back under the bar at document max, where the
        // valve then arms rows over it. The `0px` fallbacks
        // keep CSS and the JS token read agreeing when no shell hosts the table. `scroll-margin-top`
        // gives scrolls aimed at the window itself the same floor.
        // The nominal 420/480/520 is a flex BASIS, not a height: through the page → card → tabs →
        // panel → frame flex chain the window grows into whatever would otherwise be blank under the
        // card on a tall viewport (cert R2-B-3), and where there is nothing to grow into the basis is
        // exactly the old height. The cap above still binds either way.
        className={`flex max-h-[calc(100dvh-var(--charity-app-bar-h,0px)-32px)] min-h-0 flex-[1_1_420px] scroll-mt-[var(--charity-app-bar-h,0px)] flex-col overflow-hidden [contain:paint] sm:flex-[1_1_480px] @min-[988px]:flex-[1_1_520px] ${identityWidthVariable(tab)}`}
        {...testIdProp(`${STATEMENT_PREFIX}-table-window`)}
      >
        <Table
          role="presentation"
          // Below the desktop lane the table is exactly the sum of its specified widths — a 52rem
          // floor would spread slack into the pinned pair — and from it the ratified minimum returns.
          className="table-fixed @min-[988px]:min-w-[52rem]"
          testId={`${STATEMENT_PREFIX}-visible-header`}
          // The same gutter reservation as the row window below, so the two rails' scrollports are
          // the same width and their equal canvases give them one reachable maximum (CPA-WEB66).
          // The band colour on the window itself paints the reserved strip, which no cell reaches.
          wrapperClassName="shrink-0 overflow-hidden bg-neutral-50 [scrollbar-gutter:stable]"
          wrapperTestId={`${STATEMENT_PREFIX}-header-window`}
          wrapperRef={headerWindowRef}
        >
          <VisibleHeader tab={tab} columns={columns} />
        </Table>
        <Table
          // Below the desktop lane the body is FIXED-layout, so every specified width above is exact
          // and the table is precisely their sum — auto layout compressed the money columns to their
          // content at 768 (Net 93px against a 142px spec) and put the cut on Fees' padding instead
          // of on Gross' figures. From the desktop lane the content-aware auto layout and the 52rem
          // floor return.
          className="@max-[988px]:table-fixed @min-[988px]:min-w-[52rem]"
          testId={`${STATEMENT_PREFIX}-table`}
          wrapperClassName="min-h-0 flex-1 overflow-x-auto overflow-y-hidden overscroll-x-contain overscroll-y-auto [scrollbar-gutter:stable] outline-none focus-visible:shadow-ring focus-visible:outline-solid focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-500"
          wrapperTestId={`${STATEMENT_PREFIX}-row-window`}
          wrapperProps={{
            tabIndex: 0,
            role: 'region',
            'aria-label': 'Donation rows',
          }}
          wrapperRef={rowWindowRef}
        >
          <BodyColumns columnCount={columnCount} />
          <SemanticHeader tab={tab} columns={columns} />
          {view.items.length === 0 ? (
            <TBody>
              <TR>
                {/* Spans the gutter column too, so the lane's cell is the whole row. */}
                <TD colSpan={columnCount + 1} className="h-auto p-0">
                  <div
                    className="sticky left-0 w-[100cqw] max-w-[100cqw]"
                    {...testIdProp(`${STATEMENT_PREFIX}-empty-lane`)}
                  >
                    {emptyState}
                  </div>
                </TD>
              </TR>
            </TBody>
          ) : (
            view.items.map((item) => (
              <TBody key={itemKey(item)} id={bodyId(item)}>
                <ItemRows
                  item={item}
                  tab={tab}
                  columns={columns}
                  columnCount={columnCount}
                  index={index}
                  charityId={charityId}
                  formatDate={formatDate}
                  dateBoxClass={dateBoxClass}
                  expandedKeys={expandedKeys}
                  onToggle={onToggle}
                  fundraiserPanelsEnabled={fundraiserPanelsEnabled}
                />
              </TBody>
            ))
          )}
        </Table>
      </div>
    </div>
  );
}

function itemKey(item: StatementItem): string {
  return item.kind === 'group' ? item.group.key : `donation:${item.donation.id}`;
}

function bodyId(item: StatementItem): string {
  return `${STATEMENT_PREFIX}-section-${slug(itemKey(item))}`;
}

/** A DOM-id-safe form of an expansion key (which carries `:` separators). */
function slug(key: string): string {
  return key.replace(/[^a-zA-Z0-9_-]+/g, '-');
}

// ── The Status column header + its explainer ────────────────────────────────────────────────────────

function StatusHeader({ labelHidden = false }: { labelHidden?: boolean }) {
  return (
    <span className="inline-flex items-center gap-4">
      <span aria-hidden={labelHidden || undefined}>{TRAILING_HEADER.status}</span>
      <Popover.Root>
        <Popover.Trigger
          aria-label={STATUS_EXPLAINER_TRIGGER_LABEL}
          className="inline-flex size-20 items-center justify-center rounded-md text-icon outline-none transition-colors hover:text-text-secondary focus-visible:shadow-ring focus-visible:outline-solid focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-500"
          {...testIdProp(`${STATEMENT_PREFIX}-status-info`)}
        >
          <Icon icon={Info} size={13} aria-hidden />
        </Popover.Trigger>
        <Popover.Content
          align="end"
          className="w-[320px] rounded-md border-border-subtle px-16 py-14 shadow-md"
          testId={`${STATEMENT_PREFIX}-status-explainer`}
        >
          <Popover.Title className="mb-10 block font-sans text-s12 font-w600 uppercase tracking-wide text-text-tertiary">
            {STATUS_EXPLAINER_TITLE}
          </Popover.Title>
          <div className="flex flex-col gap-10">
            {STATUS_CATEGORIES.map((category) => (
              <div key={category.key} className="flex items-start gap-10">
                {/* The recipe's `whitespace-nowrap` stands: a legend pill that wrapped mid-label
                    ("Preparing to / send") taught a different word than the table's badge (cert
                    R1-A-4). The explainer beside it is the line that wraps. */}
                <Badge
                  tone={category.tone}
                  dot
                  className="mt-2 shrink-0"
                  testId={`${STATEMENT_PREFIX}-status-legend-pill`}
                >
                  {category.label}
                </Badge>
                <span className="font-sans text-s12 leading-relaxed tracking-givmo text-text-secondary">
                  {category.explainer}
                </span>
              </div>
            ))}
          </div>
        </Popover.Content>
      </Popover.Root>
    </span>
  );
}

/** A row-level (i) — used on a non-sent grant parent and on the unshared donor bucket. */
function RowExplainer({ text, label }: { text: string; label: string }) {
  return (
    <Popover.Root>
      <Popover.Trigger
        aria-label={label}
        className="inline-flex size-20 items-center justify-center rounded-md text-icon outline-none transition-colors hover:text-text-secondary focus-visible:shadow-ring focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
        {...testIdProp(`${STATEMENT_PREFIX}-row-info`)}
      >
        <Icon icon={Info} size={14} aria-hidden />
      </Popover.Trigger>
      <Popover.Content
        align="start"
        className="w-[280px] rounded-md border-border-subtle px-16 py-14 shadow-md"
        testId={`${STATEMENT_PREFIX}-row-explainer`}
      >
        <span className="font-sans text-s12 leading-relaxed tracking-givmo text-text-secondary">
          {text}
        </span>
      </Popover.Content>
    </Popover.Root>
  );
}

// ── Rows ────────────────────────────────────────────────────────────────────────────────────────────

interface RowContext {
  tab: StatementTab;
  columns: readonly ColumnKey[];
  columnCount: number;
  index: StatementIndex;
  charityId: string;
  formatDate: (iso: string | null | undefined) => string;
  /** The date-box width for this statement's date form (CPA-WEB61). */
  dateBoxClass: string;
  expandedKeys: ReadonlySet<string>;
  onToggle: (key: string) => void;
  fundraiserPanelsEnabled: boolean;
}

function ItemRows({ item, ...context }: { item: StatementItem } & RowContext) {
  if (item.kind === 'donation') {
    // A top-level donation row (the All view). No indent rule — it is not inside anything.
    return <DonationRow donation={item.donation} indent="none" {...context} />;
  }
  return <GroupRows group={item.group} level={1} {...context} />;
}

function GroupRows({
  group,
  level,
  ...context
}: { group: StatementGroup; level: 1 | 2 } & RowContext) {
  const expanded = context.expandedKeys.has(group.key);
  // The organizer's message/contact panel belongs to the views ABOUT fundraisers — All (where a
  // fundraiser is a top-level row) and By fundraiser. It is deliberately absent from By grant: that
  // view exists to reconcile a deposit against a bank statement, and a paragraph of supporter prose
  // between two money rows is exactly the noise that makes a reconciliation view unusable. The
  // fundraiser's OWN chevron is the sole disclosure: expanded mounts panel + donations; collapsed
  // unmounts both. There is no nested panel state or control.
  const showsPanel =
    context.fundraiserPanelsEnabled &&
    expanded &&
    group.kind === 'fundraiser' &&
    group.fundraiserRef !== null &&
    (context.tab === 'all' || context.tab === 'fundraiser');

  return (
    <Fragment>
      {level === 1 ? (
        <TopGroupRow group={group} expanded={expanded} {...context} />
      ) : (
        <BucketRow group={group} expanded={expanded} {...context} />
      )}
      {showsPanel && (
        <StatementFundraiserPanel
          charityId={context.charityId}
          fundraiserRef={group.fundraiserRef as string}
          render={(panel) => (
            <tr {...testIdProp(`${STATEMENT_PREFIX}-panel-row`)}>
              <td className={MOBILE_TOGGLE_CELL} />
              <td
                colSpan={context.columnCount - 1}
                className="relative bg-surface pb-0 pl-12 pr-8 pt-16 sm:pl-[40px] sm:pr-20"
              >
                <span className="hidden sm:block">
                  <IndentRule />
                </span>
                {/* The colspan cell inherits the financial table's wide canvas, so constraining that
                    cell cannot keep ordinary narrative prose inside the visible scrollport. The
                    table frame is an inline-size query container: `cqw` therefore follows the REAL
                    content lane through the `lg` sidebar transition rather than the browser viewport.
                    The inner wrapper keeps the established sticky anchor (40px on the phone: the 28px
                    toggle slot plus this cell's 12px padding). Phones subtract that
                    same inset from the visible lane; from `sm` through narrow desktop its 84px natural
                    row inset plus its query-relative width equals the scrollport exactly, while the
                    sticky inset keeps it in view as the table pans. From the desktop lane the measured table lane can hold the ordinary
                    cell and the legacy desktop layout resumes. Financial columns remain on their
                    wider, independently pannable canvas throughout. */}
                <div
                  className="sticky left-[40px] z-10 w-[calc(100cqw-40px)] max-w-[calc(100cqw-40px)] bg-surface sm:left-[84px] sm:w-[calc(100cqw-84px)] sm:max-w-[calc(100cqw-84px)] @min-[988px]:static @min-[988px]:left-auto @min-[988px]:z-auto @min-[988px]:w-auto @min-[988px]:max-w-none"
                  {...testIdProp(`${STATEMENT_PREFIX}-panel-content`)}
                >
                  {panel}
                </div>
              </td>
              <BodyGutterCell />
            </tr>
          )}
        />
      )}
      {expanded && (
        <Fragment>
          {group.groups.map((child) => (
            <GroupRows key={child.key} group={child} level={2} {...context} />
          ))}
          {group.donations.map((donation) => (
            <DonationRow
              key={donation.id}
              donation={donation}
              indent={level === 1 ? 'child' : 'grandchild'}
              {...context}
            />
          ))}
        </Fragment>
      )}
    </Fragment>
  );
}

/** The vertical rule that ties indented rows to the group above them. */
function IndentRule() {
  return (
    <span
      aria-hidden
      className="absolute bottom-0 left-8 top-0 w-2 bg-border sm:left-28"
      {...testIdProp(`${STATEMENT_PREFIX}-indent-rule`)}
    />
  );
}

/**
 * A disclosure toggle. `controls` is OPTIONAL and deliberately absent on nested buckets.
 *
 * `<tbody id>` boundaries are per TOP-LEVEL item, so a top-level toggle has a real element holding
 * every row it reveals. A nested bucket has no such element — its rows are siblings inside the
 * parent's `<tbody>` — and pointing `aria-controls` at an id that does not exist is worse than
 * omitting it: `aria-expanded` alone is a complete disclosure contract, while a dangling IDREF is an
 * `aria-valid-attr-value` violation that tells a screen reader to look for something that is not
 * there. Splitting each bucket into its own `<tbody>` would fix the child at the cost of the parent,
 * whose controlled region would then be spread across several elements.
 */
function Toggle({
  expanded,
  onToggle,
  controls,
  label,
}: {
  expanded: boolean;
  onToggle: () => void;
  controls?: string;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-controls={controls}
      aria-label={label}
      onClick={onToggle}
      className="inline-flex size-24 shrink-0 items-center justify-center rounded-md text-icon outline-none transition-colors hover:bg-surface-hover hover:text-text-secondary focus-visible:shadow-ring focus-visible:outline-solid focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-500"
      {...testIdProp(`${STATEMENT_PREFIX}-toggle`)}
    >
      <Icon icon={expanded ? ChevronDown : ChevronRight} size={16} aria-hidden />
    </button>
  );
}

/**
 * The full-width rule above a top-level group row, per view (D-CP-60, Jonathan 2026-09-04: "Can you
 * get rid of the full horizontal lines bording the fundraiser row? it makes it look like the
 * non-fundraiser donations are part of the fundraiser when fundraiser row is in collappsed state").
 * In All the top-level groups are fundraisers sitting AMONG the direct donations, and a top-level
 * donation row draws no line of its own (each is alone in its `<tbody>`, so the row recipe's
 * `last:border-b-0` removes even the hairline); a collapsed fundraiser under a full-width rule was
 * therefore the only line on the screen, and it read as a fence over the rows beneath it. So there
 * the group row draws nothing and reads as one row among the donations. In By grant, By fundraiser
 * and By donor every row belongs to the group above it, so the rule is honest and stays.
 */
const GROUP_ROW_RULE: Record<StatementTab, string> = {
  all: '',
  grant: 'border-t border-border',
  fundraiser: 'border-t border-border',
  donor: 'border-t border-border',
};

function TopGroupRow({
  group,
  charityId,
  expanded,
  tab,
  columns,
  onToggle,
}: { group: StatementGroup; expanded: boolean } & RowContext) {
  return (
    <TR className={GROUP_ROW_RULE[tab]} testId={`${STATEMENT_PREFIX}-group-row`}>
      {orderedCells(columns, {
        // `@min-[988px]:pl-20 @min-[988px]:pr-0` repeats the `sm:` pair because the recipe's
        // `@min-[988px]:px-16` reset would
        // otherwise win the cascade at desktop and widen the column by 5px.
        toggle: (
          <TD
            key="toggle"
            className={`${MOBILE_TOGGLE_CELL} sm:pl-20 sm:pr-0 @min-[988px]:pl-20 @min-[988px]:pr-0`}
          >
            {group.toggleable && (
              <Toggle
                expanded={expanded}
                onToggle={() => onToggle(group.key)}
                controls={`${STATEMENT_PREFIX}-section-${slug(group.key)}`}
                label={`${expanded ? 'Collapse' : 'Expand'} ${group.label}`}
              />
            )}
          </TD>
        ),
        identity: (
          <TD key="identity" className={MOBILE_IDENTITY_CELL}>
            <GroupLabel group={group} tab={tab} charityId={charityId} />
          </TD>
        ),
        ...moneyCells(group.totals, true),
        // A GRANT's status lives here, on the grant's own row (D-CP-55): the transaction is the
        // thing that has a status, and its donations beneath leave the cell empty. Every other
        // group kind — a fundraiser's or a donor's donations may sit in different grants — carries
        // no status of its own, and the cell stays empty.
        status: (
          <TD key="status" className={COMPACT_STATUS_CELL}>
            {group.labelKind === 'status' && <GroupStatus group={group} />}
          </TD>
        ),
      })}
    </TR>
  );
}

function BucketRow({
  group,
  charityId,
  expanded,
  columns,
  onToggle,
}: { group: StatementGroup; expanded: boolean } & RowContext) {
  return (
    <TR testId={`${STATEMENT_PREFIX}-bucket-row`}>
      {orderedCells(columns, {
        toggle: <TD key="toggle" className={MOBILE_TOGGLE_CELL} />,
        identity: (
          <TD key="identity" className={`${MOBILE_IDENTITY_CELL} pl-12 sm:pl-32`}>
            <IndentRule />
            {/* The chevron owns a fixed 24px slot on the label's FIRST line at every width; the label
                wraps beside it, never above it (cert R2-A-4). `min-h-24` centres a one-line label on
                the chevron and lets a wrapped label start on its top line. On the phone the indent is
                12px (the rule sits at 8) and the gap 4px, so a 112px identity still gives the label
                64px — enough to keep "donations" whole. */}
            <div className="flex min-w-0 items-start gap-4 sm:gap-8">
              {/* No `controls`: this bucket's rows live inside its PARENT's <tbody>, so there is no
                  element to point at. See the Toggle docstring. */}
              <Toggle
                expanded={expanded}
                onToggle={() => onToggle(group.key)}
                label={`${expanded ? 'Collapse' : 'Expand'} ${group.label}`}
              />
              {/* The label lane WRAPS everywhere below the desktop lane — never `nowrap` from `sm`
                  (CPA-WEB63). The count is one unbreakable unit and the name is `min-w-0
                  break-words`, so in a `nowrap` lane the name absorbed the whole shortfall: at the
                  160px identity floor (viewports 640 to ~700) the lane is 79px, the count took ~70
                  of it, and "Direct donations" broke one letter per line in the ~10px left, in a
                  274px-tall row. Wrapping, the count drops beneath the name as a whole — the form
                  the phone already had — and the name breaks between its words. Only the desktop
                  lane, an auto-layout table that sizes the column to its content, can promise one
                  line, so `nowrap` and the count's dot (see `Separated`) return together there.
                  `sm:gap-y-2` is the stack's gap from `sm` — every sibling's name / secondary-line
                  gap in this table (`GroupLabel`, the fundraiser bucket above). */}
              <div className="flex min-h-24 min-w-0 flex-1 flex-wrap items-center gap-6 sm:gap-x-8 sm:gap-y-2 @min-[988px]:flex-nowrap">
                {group.kind === 'fundraiser' ? (
                  <span className="flex min-w-0 flex-col gap-2">
                    <span className="inline-flex min-w-0 flex-wrap items-center gap-6 sm:flex-nowrap sm:gap-8">
                      <GroupName
                        group={group}
                        charityId={charityId}
                        weight="w500"
                        controlLayout="inline"
                      />
                    </span>
                    <span className="flex min-w-0 flex-wrap items-center gap-6">
                      <Badge
                        tone="brand"
                        size="sm"
                        className="rounded-full"
                        testId={`${STATEMENT_PREFIX}-fundraiser-type`}
                      >
                        Fundraiser
                      </Badge>
                      <span
                        className="whitespace-nowrap font-sans text-s12 tracking-givmo text-text-tertiary"
                        {...testIdProp(`${STATEMENT_PREFIX}-group-count`)}
                      >
                        {groupCountLabel(group)}
                      </span>
                    </span>
                  </span>
                ) : (
                  <Fragment>
                    <GroupName group={group} charityId={charityId} weight="w500" />
                    <Separated
                      dot="desktop-lane"
                      className="font-sans text-s13 tracking-givmo text-text-tertiary"
                      testId={`${STATEMENT_PREFIX}-group-count`}
                    >
                      {groupCountLabel(group)}
                    </Separated>
                  </Fragment>
                )}
              </div>
            </div>
          </TD>
        ),
        ...moneyCells(group.totals),
      })}
    </TR>
  );
}

/**
 * A grant row's status, in the Status column (D-CP-55): the server's status-first label as the tone
 * badge — the one fact a reconciler reads on this row — with the (i) for the states a reader can
 * misread (`grantRowExplainer`). Below `sm` the badge wraps inside the compact 88px cell with its
 * dot standing down, exactly as a donation row's badge does in the other views, and the (i) wraps
 * under it.
 */
function GroupStatus({ group }: { group: StatementGroup }) {
  return (
    <span className="inline-flex max-w-full flex-wrap items-center gap-4 sm:gap-6">
      <Badge
        tone={group.tone}
        dot
        size="md"
        className={`h-auto whitespace-normal py-3 ${COMPACT_STATUS_BADGE}`}
        testId={`${STATEMENT_PREFIX}-group-status`}
      >
        {group.label}
      </Badge>
      {group.explainer && (
        <RowExplainer text={group.explainer} label={`What "${group.label}" means`} />
      )}
    </span>
  );
}

function GroupLabel({
  group,
  tab,
  charityId,
}: {
  group: StatementGroup;
  tab: StatementTab;
  charityId: string;
}) {
  // The donor lens is the one whose secondary line carries TWO facts — a donor's email and count, or
  // the unshared bucket's count and its suffix — in a ~142px column (see the width note above), which
  // cannot hold both on one line. Bound as one "· token" unit the second fact wrapped whole and BEGAN
  // its line with the dot (cert R1-A-4 follow-up; Jonathan, 2026-09-01), so there the two facts are
  // stacked lines with no separator glyph at all — the flex-col form the pinned phone column already
  // uses, and a donor header is a multi-line form in the design anyway. Every other lens keeps the
  // design's single `nowrap` count, which is what holds the row rhythm there.
  const stacked = tab === 'donor' ? donorSecondaryLines(group) : null;
  // A grant has no name of its own — the server's label is status-first ("Sent Aug 3, 2026") — and
  // D-CP-55 puts that status in the Status column (`GroupStatus`). So the grant row is named by the
  // transaction word the tab, the column header and the spreadsheet already use, and its secondary
  // line carries the count and the rail: "5 donations · bank transfer" from `sm`, the two facts as
  // stacked lines below it (the donor lens's form, for the same reason: a "· token" unit that wraps
  // whole begins its line with the dot). The one status-labelled group that is NOT a grant — the
  // server's "unassigned" bucket, money with no grant record yet — is named `UNASSIGNED_GRANT_LABEL`
  // ("Not yet designated", plan 18 §3.1) instead: a row whose own explainer says it is not part of a
  // transfer cannot be called a Grant. Decided on `isUnassigned` (the wire's ref), never the label.
  const isGrant = group.labelKind === 'status';

  return (
    <div className="flex flex-col gap-2">
      <span className="inline-flex min-w-0 flex-wrap items-center gap-6 sm:gap-8 @min-[988px]:flex-nowrap @min-[988px]:whitespace-nowrap">
        {isGrant ? (
          <span
            className="min-w-0 font-w600 text-text-strong"
            {...testIdProp(`${STATEMENT_PREFIX}-group-name`)}
          >
            {group.isUnassigned ? UNASSIGNED_GRANT_LABEL : FIRST_COLUMN_LABEL.grant}
          </span>
        ) : (
          <GroupName group={group} charityId={charityId} weight="w600" />
        )}
        {!isGrant && group.explainer && (
          <RowExplainer text={group.explainer} label={`What "${group.label}" means`} />
        )}
      </span>
      {isGrant ? (
        <span
          className="flex min-w-0 flex-col gap-2 font-sans text-s12 tracking-givmo text-text-tertiary sm:flex-row sm:flex-wrap sm:items-center sm:gap-6"
          {...testIdProp(`${STATEMENT_PREFIX}-group-count`)}
        >
          <span className="whitespace-nowrap">{groupCountLabel(group)}</span>
          {group.method && (
            <Fragment>
              {' '}
              <Separated dot="sm">{group.method}</Separated>
            </Fragment>
          )}
        </span>
      ) : group.kind === 'fundraiser' ? (
        // Fundraiser is a row TYPE, not a status or part of its name. Jonathan's reveal ruling puts
        // the quiet classifier on the secondary line beside the count in every fundraiser grouping,
        // including All; the former one-line All exception is deliberately gone.
        // The pill never wraps (the Badge recipe is `whitespace-nowrap`); when the 132px phone column
        // cannot hold both, the count wraps onto the next line as a whole (cert R1-A-2).
        <span className="flex min-w-0 flex-wrap items-center gap-6">
          <Badge
            tone="brand"
            size="sm"
            className="rounded-full"
            testId={`${STATEMENT_PREFIX}-fundraiser-type`}
          >
            Fundraiser
          </Badge>
          <span
            className="whitespace-nowrap font-sans text-s12 tracking-givmo text-text-tertiary"
            {...testIdProp(`${STATEMENT_PREFIX}-group-count`)}
          >
            {groupCountLabel(group)}
          </span>
        </span>
      ) : stacked ? (
        // Two lines, no "·": the space between the spans is not painted inside a flex column but keeps
        // the two facts as separate words in the DOM text and the accessible name. Below `sm` the
        // first line (an email, usually) is one ellipsized line, as a donation row's email already is
        // (cert R1-A-2); from `sm` up it wraps at words rather than painting past the cell.
        <span
          className="flex min-w-0 flex-col gap-2 font-sans text-s12 tracking-givmo text-text-tertiary"
          {...testIdProp(`${STATEMENT_PREFIX}-group-count`)}
        >
          <span className="min-w-0 truncate sm:overflow-visible sm:whitespace-normal sm:break-words">
            {stacked[0]}
          </span>{' '}
          <span>{stacked[1]}</span>
        </span>
      ) : (
        <span
          className="whitespace-nowrap font-sans text-s12 tracking-givmo text-text-tertiary"
          {...testIdProp(`${STATEMENT_PREFIX}-group-count`)}
        >
          {groupCountLabel(group)}
        </span>
      )}
    </div>
  );
}

/** The donor lens's two-fact secondary line — email then count, or count then the unshared suffix — or null where the count stands alone. */
function donorSecondaryLines(group: StatementGroup): readonly [string, string] | null {
  if (group.kind === 'unshared') return [groupCountLabel(group), UNSHARED_SECONDARY_SUFFIX];
  if (group.email) return [group.email, groupCountLabel(group)];
  return null;
}

/**
 * Where a separator's dot is painted. LITERAL class strings — Tailwind reads source as text. The
 * dot stays in the DOM text (`textContent`) at every width; the accessible name carries it only
 * where it is painted, because a `display:none` span is excluded from the name — so a reader hears
 * "1 donation", never a lone "·", wherever the dot is hidden (below `sm` for `sm`, below the
 * desktop lane for `desktop-lane`).
 */
const SEPARATOR_DOT = {
  sm: 'hidden sm:inline',
  'desktop-lane': 'hidden @min-[988px]:inline',
} as const;

/**
 * "· token" as ONE unbreakable unit, so a separator travels with what it introduces and is never
 * stranded at the end of a line (cert R1-A-4). Callers put an ordinary space BEFORE it, which is
 * the only break opportunity. Where the unit sits on its own line — below `sm` the pinned phone
 * column wraps these chunks as whole flex items — the dot itself stands down: a line that begins
 * with a lone "·" reads as a bullet, not a separator. `dot` names where it returns: from `sm`, or,
 * for a unit that can still wrap beneath its neighbour across the bounded lane (the By-grant bucket
 * count, CPA-WEB63), only from the desktop lane.
 */
function Separated({
  children,
  dot,
  className = '',
  testId,
}: {
  children: ReactNode;
  dot: keyof typeof SEPARATOR_DOT;
  className?: string;
  testId?: string;
}) {
  return (
    <span className={`whitespace-nowrap ${className}`.trim()} {...testIdProp(testId)}>
      <span className={SEPARATOR_DOT[dot]}>· </span>
      {children}
    </span>
  );
}

/**
 * A group's name with its public link, if any, bound to the name's LAST word: the ↗ icon is part of
 * the word's line box, so a wrapping phone name can never leave the icon alone on a line of its own
 * (cert R1-A-2). Long names still wrap word by word before that.
 */
function GroupName({
  group,
  charityId,
  weight,
  controlLayout = 'beside',
}: {
  group: StatementGroup;
  charityId: string;
  weight: 'w500' | 'w600';
  controlLayout?: 'beside' | 'inline';
}) {
  const label = group.label;
  const url = group.publicUrl;
  const moduleLink =
    group.kind === 'fundraiser' && group.fundraiserRef !== null && group.ownerKind === 'org' ? (
      <CharityFundraiserReportLink
        charityId={charityId}
        fundraiserId={group.fundraiserRef}
        ownerKind={group.ownerKind}
        testId="statement-fundraiser-module-link"
        className={controlLayout === 'inline' ? 'ml-2 align-middle sm:ml-8' : undefined}
      />
    ) : null;
  const words = label.split(' ');
  const last = words.pop() ?? '';
  const head = words.length > 0 ? `${words.join(' ')} ` : '';
  const tail = glueTail(last);
  const publicName = (
    <span
      className={`min-w-0 break-words ${weight === 'w600' ? 'font-w600' : 'font-w500'} text-text-strong @min-[988px]:break-normal`}
    >
      {head}
      {url ? (
        // The last word stays BREAKABLE — a user-authored 26-character word wraps at the cell instead
        // of painting into Net (review F3) — and the ↗ is glued only to its TAIL: the last few
        // characters and the icon share the one atomic `inline-flex … nowrap` unit the whole word
        // used to (unchanged for a word of eight characters or fewer, so the icon's centring on the
        // line is exactly what it was), and the rest of the word can break before it. A WORD JOINER
        // alone does not do this: Chrome's line breaker still breaks before an atomic inline when it
        // does not fit, and the probe showed the icon alone under "…FundraiserName" at 390.
        <span {...testIdProp(`${STATEMENT_PREFIX}-group-name-end`)}>
          {tail.head}
          <span className="inline-flex items-center gap-6 whitespace-nowrap sm:gap-8">
            {tail.tail}
            <PublicLink url={url} name={label} />
          </span>
        </span>
      ) : (
        last
      )}
    </span>
  );
  // The touch target owns a separate slot, so it cannot enlarge the public
  // link's last-word line box or pull that existing arrow away from the name.
  return moduleLink ? (
    controlLayout === 'inline' ? (
      <span className="min-w-0 max-w-full">
        {publicName}
        {moduleLink}
      </span>
    ) : (
      <span className="inline-flex min-w-0 max-w-full items-center gap-6 sm:gap-8">
        {publicName}
        {moduleLink}
      </span>
    )
  ) : (
    publicName
  );
}

/**
 * How a name's last word is split so the ↗ can be glued to it: a word of up to twice `GLUE_TAIL`
 * characters is glued whole (the old unit, unchanged for ordinary names); a longer one gives its last
 * `GLUE_TAIL` characters to the `nowrap` unit and leaves the rest breakable (review F3).
 */
export const GLUE_TAIL = 4;
export function glueTail(word: string): { head: string; tail: string } {
  const at = word.length > 2 * GLUE_TAIL ? word.length - GLUE_TAIL : 0;
  return { head: word.slice(0, at), tail: word.slice(at) };
}

/** The fundraiser's public page. The URL is server-composed and rendered verbatim (D-ATT-6). */
function PublicLink({ url, name }: { url: string; name: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      aria-label={`Open ${name} (opens in a new tab)`}
      className="inline-flex size-20 items-center justify-center rounded-md text-icon outline-none transition-colors hover:text-text-secondary focus-visible:shadow-ring focus-visible:outline-solid focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-500"
      {...testIdProp(`${STATEMENT_PREFIX}-fundraiser-link`)}
    >
      <Icon icon={ExternalLink} size={14} aria-hidden />
    </a>
  );
}

/**
 * One row's cells in the spec's order, then the row's cell in the body's gutter column; a column the
 * row has nothing for is an empty cell.
 */
function orderedCells(
  columns: readonly ColumnKey[],
  cells: Partial<Record<ColumnKey, ReactNode>>,
): ReactNode[] {
  return [...columns.map((key) => cells[key] ?? <TD key={key} />), <BodyGutterCell key="gutter" />];
}

/**
 * Row emphasis follows the headline: the page's figure is NET TO YOU, so net carries the strong
 * colour in every row and the 600 weight on a group row, while GROSS and FEES read as the secondary
 * arithmetic behind it (cert R2-A-1). Column order is untouched.
 */
function moneyCells(
  totals: { gross_total: string; total_fee: string; net_total: string },
  strong = false,
): Pick<Record<ColumnKey, ReactNode>, 'gross' | 'fee' | 'net'> {
  const secondary = strong ? 'font-w400 text-text-secondary' : 'text-text-secondary';
  return {
    gross: (
      <TD key="gross" align="right" numeric className={COMPACT_MONEY_CELL}>
        <span className={secondary} {...testIdProp(`${STATEMENT_PREFIX}-gross`)}>
          {displayMoney(totals.gross_total)}
        </span>
      </TD>
    ),
    fee: (
      <TD key="fee" align="right" numeric className={COMPACT_MONEY_CELL}>
        <span className={secondary} {...testIdProp(`${STATEMENT_PREFIX}-fee`)}>
          {displayMoney(totals.total_fee)}
        </span>
      </TD>
    ),
    net: (
      <TD key="net" align="right" numeric className={COMPACT_NET_CELL}>
        <span
          className={strong ? 'font-w600 text-text-strong' : 'text-text-strong'}
          {...testIdProp(`${STATEMENT_PREFIX}-net`)}
        >
          {displayMoney(totals.net_total)}
        </span>
      </TD>
    ),
  };
}

function DonationRow({
  donation,
  indent,
  tab,
  columns,
  index,
  formatDate,
  dateBoxClass,
}: {
  donation: Section321StatementDonationOut;
  indent: 'none' | 'child' | 'grandchild';
} & RowContext) {
  const grant = index.grants.get(donation.grant_ref);
  // In By grant a donation row shows no status of its own (D-CP-55), so none is composed for it.
  const status = tab === 'grant' ? null : donationStatusBadge(grant, formatDate);
  const fundraiserName = donation.fundraiser_ref
    ? index.fundraisers.get(donation.fundraiser_ref)?.name
    : null;
  // The donor is already named by the group in the By-donor view; repeating it on every child row
  // would restate the header N times.
  const showDonor = tab !== 'donor';

  return (
    <TR testId={`${STATEMENT_PREFIX}-donation-row`}>
      {orderedCells(columns, {
        toggle: <TD key="toggle" className={MOBILE_TOGGLE_CELL} />,
        identity: (
          <TD
            key="identity"
            className={
              indent === 'none'
                ? `${MOBILE_IDENTITY_CELL} pl-8 sm:pl-16`
                : indent === 'child'
                  ? `${MOBILE_IDENTITY_CELL} pl-20 sm:pl-[40px]`
                  : `${MOBILE_IDENTITY_CELL} pl-28 sm:pl-[64px]`
            }
          >
            {indent !== 'none' && <IndentRule />}
            <span className="flex min-w-0 flex-col gap-2 sm:inline @min-[988px]:whitespace-nowrap">
              {/* The date box, and the gutter that keeps the donor off it (CPA-WEB61). `dateBoxClass`
                  is FIXED so the donor starts at the same x on every row — the single 56px it was
                  before held the short "May 30" form and not the year-bearing "May 30, 2026" one
                  production serves, which is how the donor came to paint over the date.
                  The two forms carry different variants on purpose (`statementDateBoxClass`): the
                  no-year 56px box rides from `sm` at every width, unchanged by this fix, because it
                  fits the bounded lane's 80px worst case; the year-bearing 90px box does not fit it,
                  so it is desktop-lane-only and that form is content-sized below, with `pr-8` as its
                  gutter. There the donor sits INLINE after the date and wraps beneath only when the
                  name is long and the column narrow — stacking is the phone layout (`flex-col`
                  above, below `sm`), where neither the width nor the gutter applies. `pr-8` rides
                  inside both fixed boxes, which clear their own worst case by the same ~15px. */}
              <span
                className={`inline-block w-auto font-sans text-s12 tabular-nums tracking-givmo text-text-tertiary sm:pr-8 sm:text-s13 ${dateBoxClass}`}
                {...testIdProp(`${STATEMENT_PREFIX}-donation-date`)}
              >
                {formatDate(donation.date_made)}
              </span>
              {showDonor && <DonorCell donation={donation} />}
            </span>
          </TD>
        ),
        ...moneyCells({
          gross_total: donation.gross,
          total_fee: donation.total_fee,
          net_total: donation.net,
        }),
        designation: (
          <TD key="designation">
            <Muted value={donation.designation} />
          </TD>
        ),
        fundraiser: (
          <TD key="fundraiser">
            <Muted value={fundraiserName} />
          </TD>
        ),
        // In By grant the status is the GRANT's and sits on the grant's own row (D-CP-55, Jonathan:
        // "no point in having individual donation status on the grant table"). The cell stays BLANK
        // — not a dash, which would say "no status" about a leg whose status is on the row above.
        // Every other view shows it per row: there nothing else on the row names the donation's grant.
        status:
          tab === 'grant' ? (
            <TD key="status" className={COMPACT_STATUS_CELL} />
          ) : (
            <TD key="status" className={COMPACT_STATUS_CELL}>
              {status ? (
                <Badge
                  tone={status.tone}
                  dot
                  // `h-auto py-2`: the Badge recipe is a FIXED `h-20`, so any future server-authored
                  // label that wraps must grow inside its tinted background rather than spill beyond
                  // it. The statement's current labels, including "Returned", stay on one line.
                  // Alignment remains `items-center` (the recipe's), which centres the dot on that
                  // normal one-line form and on the block if a longer label ever wraps without this
                  // client rewording it.
                  className={`h-auto whitespace-normal py-2 ${COMPACT_STATUS_BADGE}`}
                  testId={`${STATEMENT_PREFIX}-donation-status`}
                >
                  {status.label}
                </Badge>
              ) : (
                <Dash />
              )}
            </TD>
          ),
      })}
    </TR>
  );
}

/**
 * The donor on a donation row. IDENTITY LAW: `donor_name`/`donor_email` are null unless the backend's
 * §321(a)(6) conjunction opened them, and this renders ONLY what arrived — an em dash otherwise, never
 * an inferred, backfilled, or placeholder identity. An email-only sharing guest (a public checkout that
 * captured no display name) is disclosed as exactly that: the email, no name.
 */
function DonorCell({ donation }: { donation: Section321StatementDonationOut }) {
  const { donor_name: name, donor_email: email } = donation;
  if (!name && !email) return <Dash />;
  return (
    <span
      className="min-w-0 break-words @min-[988px]:break-normal"
      {...testIdProp(`${STATEMENT_PREFIX}-donation-donor`)}
    >
      {name && <span className="text-text">{name}</span>}
      {name && email && (
        <Fragment>
          {' '}
          {/* Below `sm` the email is its own ellipsized line under the name — a 132px column broke
              it mid-word ("terrence.blake@ex / ample.com", cert R1-A-2). The full address stays in
              the DOM text and the accessible name. From `sm` the "· email" unit is INLINE-BLOCK and
              bound to the cell (`max-w-full`, still `truncate`), never an overflow-visible inline
              (CPA-WEB64): it sits after the name when it fits, wraps beneath as a whole when it does
              not, and ellipsizes when even a whole line cannot hold it — as an inline it painted
              past the cell's edge into Net instead (68.88px at the 160px identity floor with the
              shipped fixtures; ~300px with a long address). `align-bottom` because an inline-block
              with hidden overflow has its baseline at its bottom edge, which would lift the text;
              bottom-aligned it keeps the old inline's rhythm exactly on a shared line and 0.5px on
              its own. The desktop lane, an auto-layout table sized to its content, restores the
              inline. */}
          <Separated
            dot="sm"
            className="block truncate font-sans text-s13 text-text-tertiary sm:inline-block sm:max-w-full sm:align-bottom @min-[988px]:inline @min-[988px]:overflow-visible @min-[988px]:align-baseline"
            testId={`${STATEMENT_PREFIX}-donation-email`}
          >
            {email}
          </Separated>
        </Fragment>
      )}
      {!name && email && <span className="text-text">{email}</span>}
    </span>
  );
}

function Muted({ value }: { value: string | null | undefined }): ReactNode {
  if (!value) return <Dash />;
  return <span className="font-sans text-s13 tracking-givmo text-text-secondary">{value}</span>;
}

function Dash() {
  // `text-text-tertiary`, not an icon colour: this dash is table CONTENT — it says "not shared" /
  // "no designation" and assistive tech reads it — so it takes the text floor (4.68:1 on the card),
  // where `icon-muted` computes 2.52:1.
  return <span className="text-text-tertiary">—</span>;
}
